import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { jsonLdScript, webPage } from '@/lib/schema'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowRight } from 'lucide-react'
import { BrowserCheckCta } from '@/components/browser-check-cta'

export const Route = createFileRoute('/_site/tools/uptime-calculator')({
  component: UptimeCalculatorPage,
  head: () => ({
    meta: [
      { title: 'Uptime Calculator — Downtime per Percentage' },
      { name: 'description', content: 'How much downtime does your uptime % allow? Per-day, week, month, year presets for 99%-99.999% SLAs.' },
      { property: 'og:title', content: 'Uptime Calculator — Calculate Downtime from Uptime Percentage' },
      { property: 'og:description', content: 'Convert uptime percentage to downtime per day, week, month, and year. Free interactive calculator.' },
      { property: 'og:image', content: 'https://uptrack.app/og/tools-uptime-calculator.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/tools/uptime-calculator' },
    ],
    scripts: [
      jsonLdScript(webPage({
        name: 'Uptime Calculator',
        description: 'Calculate downtime from uptime percentage. Convert 99.99% uptime to minutes and seconds of allowed downtime.',
        url: 'https://uptrack.app/tools/uptime-calculator',
      })),
    ],
  }),
})

const presets = [99, 99.5, 99.9, 99.95, 99.99, 99.999]

function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 1) return `${(totalSeconds * 1000).toFixed(0)}ms`
  const d = Math.floor(totalSeconds / 86400)
  const h = Math.floor((totalSeconds % 86400) / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const parts: string[] = []
  if (d > 0) parts.push(`${d}d`)
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m.toFixed(0)}m`)
  if (s > 0.5 || parts.length === 0) parts.push(`${s.toFixed(1)}s`)
  return parts.join(' ')
}

function UptimeCalculatorPage() {
  const [uptime, setUptime] = useState(99.99)

  const downtimeFraction = (100 - uptime) / 100
  const perYear = downtimeFraction * 365.25 * 24 * 3600
  const perMonth = downtimeFraction * 30.44 * 24 * 3600
  const perWeek = downtimeFraction * 7 * 24 * 3600
  const perDay = downtimeFraction * 24 * 3600

  const rows = [
    { label: 'Per day', value: perDay },
    { label: 'Per week', value: perWeek },
    { label: 'Per month', value: perMonth },
    { label: 'Per year', value: perYear },
  ]

  return (
    <>
      <>
        <section className="container mx-auto px-4 pb-8 pt-12 md:pt-16">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Uptime Calculator</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">Convert an uptime percentage into actual downtime per day, week, month, and year.</p>
          </div>
        </section>

        <section className="pb-12 md:pb-16">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="rounded-lg border border-border bg-card p-5 md:p-6">
              <div className="mb-6">
                <Label htmlFor="uptime" className="mb-2 block text-sm font-medium">Uptime percentage (%)</Label>
                <Input
                  id="uptime"
                  type="number"
                  step="0.001"
                  min="0"
                  max="100"
                  value={uptime}
                  onChange={(e) => setUptime(parseFloat(e.target.value) || 0)}
                  className="mb-4"
                />
                <input
                  type="range" aria-label="Target uptime percentage"
                  min="90"
                  max="99.9999"
                  step="0.001"
                  value={uptime}
                  onChange={(e) => setUptime(parseFloat(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
              <div className="mb-6 flex flex-wrap gap-2">
                {presets.map((p) => (
                  <Button
                    key={p}
                    variant={uptime === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUptime(p)}
                  >
                    {p}%
                  </Button>
                ))}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 text-left font-medium text-muted-foreground">Period</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Allowed downtime</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.label} className="border-b border-border last:border-0">
                      <td className="py-3 font-medium">{r.label}</td>
                      <td className="py-3 text-right num text-foreground">{formatDuration(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 rounded-lg border border-border bg-card p-5 md:p-6">
              <h2 className="mb-4 text-lg font-semibold">Common uptime tiers</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 text-left font-medium text-muted-foreground">Uptime</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Downtime/year</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Downtime/month</th>
                  </tr>
                </thead>
                <tbody>
                  {presets.map((p) => {
                    const frac = (100 - p) / 100
                    return (
                      <tr key={p} className="border-b border-border last:border-0">
                        <td className="py-2 font-medium">{p}%</td>
                        <td className="py-2 text-right num text-muted-foreground">{formatDuration(frac * 365.25 * 86400)}</td>
                        <td className="py-2 text-right num text-muted-foreground">{formatDuration(frac * 30.44 * 86400)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Monitor your uptime with Uptrack</h2>
            <p className="mb-6 mt-3 max-w-xl text-body">
              Pro includes 50 monitors — know the moment your site goes down.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        <section className="container mx-auto max-w-3xl px-4 pb-16">
          <BrowserCheckCta />
        </section>
      </>
      </>
  )
}
